// Lista de promos de venta sobre el panel común: búsqueda, estado y segmentación, y la tabla en el
// orden común. Filtrar y ordenar corre en el cliente sobre lo cargado.
import type { ReactNode } from 'react';
import { CellLines, CellText, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    isDimmedLifecycleStatus,
    LifecycleListPanel,
    LifecycleNameCell,
} from '@/shared/patterns/Lifecycle';
import type { SalePromotionListItem } from '../api/promotionsContract';
import { SALE_PROMOTION_HELP } from '../constants/helpText';
import { formatInt } from '../lib/promotionsFormat';
import {
    describeScope,
    describeSegmentation,
    listStatusOf,
    LIST_FILTER_STATUSES,
    SEGMENTATION_FILTER_OPTIONS,
    type PromotionListFilters,
    type PromotionListSort,
    type PromotionSortKey,
    type SegmentationFilter,
    type StatusFilter,
} from '../lib/promotionsList';
import { describeLevers } from '../lib/leverSummary';
import {
    describeStackable,
    formatConsumed,
    formatShortValidity,
    formatValidity,
} from '../lib/promotionsRow';
import { formValuesFromPromotion } from '../lib/salePromotionForm';
import {
    isSystemPromotion,
    SYSTEM_PROMOTION_BADGE,
    SYSTEM_PROMOTION_BADGE_TITLE,
    SYSTEM_PROMOTION_SCOPE,
} from '../lib/systemPromotion';
import { ListStatusBadge } from './ListStatusBadge';
import { ALL_RETAILERS } from '../constants/retailerFilter';

/** Por debajo de este ancho la tabla scrollea dentro de su panel, en vez de cortar los encabezados. */
const TABLE_MIN_WIDTH = '560px';

export interface PromotionsListPanelProps {
    items: SalePromotionListItem[];
    loaded: boolean;
    now: Date;
    filters: PromotionListFilters;
    onFiltersChange: (filters: PromotionListFilters) => void;
    sort: PromotionListSort;
    onSortChange: (sort: PromotionListSort) => void;
    segmentNames: ReadonlyMap<string, string>;
    /** «Marca modelo» por SKU, para nombrar el regalo. */
    productNames: ReadonlyMap<string, string>;
    selectedId: string | null;
    onSelect: (promo: SalePromotionListItem) => void;
    canCreate: boolean;
    onCreate: () => void;
    emptyText: string;
    /** «Cargar más», o el aviso de falla, bajo la tabla. */
    footer?: ReactNode;
    /** Las cadenas de la lista junta; con 2+ se pintan su columna y su filtro. */
    retailers?: {
        names: ReadonlyMap<string, string>;
        value: string;
        onChange: (retailerId: string) => void;
    };
}

export function PromotionsListPanel({
    items,
    loaded,
    now,
    filters,
    onFiltersChange,
    sort,
    onSortChange,
    segmentNames,
    productNames,
    selectedId,
    onSelect,
    canCreate,
    onCreate,
    emptyText,
    footer,
    retailers,
}: PromotionsListPanelProps) {
    const byRetailer = retailers !== undefined && retailers.names.size > 1;
    const columns: DataTableColumn<SalePromotionListItem, PromotionSortKey>[] = [
        {
            key: 'name',
            label: 'Promo',
            sortKey: 'name',
            cellOverflow: 'wrap',
            cell: (promo) => <PromoCell promo={promo} now={now} productNames={productNames} />,
        },
        {
            key: 'status',
            label: 'Estado',
            sortKey: 'status',
            width: '84px',
            cellOverflow: 'nowrap',
            cellTitle: (promo) => describeStackable(promo.stackable),
            cell: (promo) => <ListStatusBadge promo={promo} now={now} />,
        },
        {
            key: 'validity',
            label: 'Vigencia',
            width: '108px',
            cellTitle: (promo) => formatValidity(promo),
            cell: (promo) => (
                <span className="text-[10.5px] leading-[1.35]">
                    {formatShortValidity(promo, now)}
                </span>
            ),
        },
        ...(byRetailer
            ? [
                  {
                      key: 'retailer',
                      label: 'Cadena',
                      width: '104px',
                      cell: (promo: SalePromotionListItem) => (
                          <span className="text-[10.5px] leading-[1.35]">
                              {retailers.names.get(promo.retailerId) ?? promo.retailerId}
                          </span>
                      ),
                  },
              ]
            : []),
        {
            key: 'segmentation',
            label: 'Segmentación',
            sortKey: 'segmentation',
            width: '114px',
            cellTitle: (promo) => describeSegmentation(promo, segmentNames),
            cell: (promo) => (
                <span className="text-[10.5px] leading-[1.35]">
                    {describeSegmentation(promo, segmentNames)}
                </span>
            ),
        },
        {
            key: 'redemptions',
            label: 'Canjes',
            sortKey: 'redemptions',
            width: '72px',
            numeric: true,
            note: 'Ventas cobradas con la promo en los últimos 30 días',
            cell: (promo) => formatInt(promo.redemptionsLast30Days),
        },
        {
            key: 'consumed',
            label: 'Consumido',
            sortKey: 'consumed',
            width: '96px',
            numeric: true,
            note: 'Dinero que la promo ya gastó, contra su tope si lo tiene',
            cell: (promo) => <ConsumedCell promo={promo} />,
        },
    ];

    return (
        <LifecycleListPanel
            search={{
                label: 'Buscar promo',
                value: filters.search,
                onChange: (search) => {
                    onFiltersChange({ ...filters, search });
                },
            }}
            status={{
                value: filters.status,
                anyValue: 'ALL',
                options: LIST_FILTER_STATUSES.map((status) => ({ value: status, status })),
                onChange: (status) => {
                    onFiltersChange({ ...filters, status: status as StatusFilter });
                },
            }}
            filters={[
                ...(byRetailer
                    ? [
                          {
                              label: 'Cadena',
                              help: SALE_PROMOTION_HELP.filterRetailer,
                              value: retailers.value,
                              options: [
                                  { value: ALL_RETAILERS, label: 'Todas las cadenas' },
                                  ...[...retailers.names.entries()]
                                      .sort(([, a], [, b]) => a.localeCompare(b, 'es-MX'))
                                      .map(([value, label]) => ({ value, label })),
                              ],
                              onChange: retailers.onChange,
                          },
                      ]
                    : []),
                {
                    label: 'Segmentación',
                    help: SALE_PROMOTION_HELP.filterSegmentation,
                    value: filters.segmentation,
                    options: SEGMENTATION_FILTER_OPTIONS,
                    onChange: (segmentation) => {
                        onFiltersChange({
                            ...filters,
                            segmentation: segmentation as SegmentationFilter,
                        });
                    },
                },
            ]}
            count={{ value: items.length, one: 'promo', many: 'promos' }}
            create={canCreate ? { label: 'Crear promoción', onClick: onCreate } : null}
            footer={footer}
            table={{
                columns,
                rows: items,
                rowKey: (promo) => promo.promotionId,
                minWidth: TABLE_MIN_WIDTH,
                caption:
                    'Promos de venta con su estado, vigencia, segmentación, canjes de los últimos 30 días y dinero consumido.',
                sort,
                onSortToggle: (key) => {
                    onSortChange(
                        sort.key === key
                            ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
                            : { key, direction: 'asc' },
                    );
                },
                isRowSelected: (promo) => promo.promotionId === selectedId,
                onRowClick: onSelect,
                isLoading: !loaded,
                skeletonRows: 6,
                empty: emptyText,
            }}
        />
    );
}

/** Nombre y palancas en una línea cada uno; el alcance va al `title`. */
function PromoCell({
    promo,
    now,
    productNames,
}: {
    promo: SalePromotionListItem;
    now: Date;
    productNames: ReadonlyMap<string, string>;
}) {
    const system = isSystemPromotion(promo);
    return (
        <LifecycleNameCell
            name={promo.name}
            detail={describeLevers(formValuesFromPromotion(promo), productNames)}
            badge={system ? SYSTEM_PROMOTION_BADGE : undefined}
            extra={
                system
                    ? [SYSTEM_PROMOTION_BADGE_TITLE, SYSTEM_PROMOTION_SCOPE]
                    : [describeScope(promo.scope)]
            }
            dimmed={isDimmedLifecycleStatus(listStatusOf(promo, now))}
        />
    );
}

/** Lo consumido; con tope, el tope abajo en gris. */
function ConsumedCell({ promo }: { promo: SalePromotionListItem }) {
    const { consumed, cap } = formatConsumed(promo);
    return cap === null ? (
        <CellText>{consumed}</CellText>
    ) : (
        <CellLines primary={consumed} secondary={cap} />
    );
}
